import React, { useState, useEffect, useCallback, memo } from 'react';
import IASideBar from './IASideBar';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import SearchAppBar from '../../common/Appbar';
import DynamicSnackbar from '../../common/Snackbar';
import roleService from '../../../services/RoleService';
import auditService from '../../../services/AuditService';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import Avatar from '@mui/material/Avatar';
import { stringAvatar } from '../../../utils/avatarUtils';
import { useParams } from 'react-router-dom'; 
import { 
    ShieldCheckIcon, 
    UserGroupIcon, 
    KeyIcon,
    MagnifyingGlassIcon,
    DocumentIcon,
    PlusIcon,
    TrashIcon,
    PencilIcon,
    CheckCircleIcon,
    AssignmentTurnedInIcon
} from '@heroicons/react/24/outline';



const CreateUserDialog = memo(({ open, onClose, formData, setFormData, roles, onSubmit }) => {
    const [errors, setErrors] = useState({
        email: '',
        first_name: '',
        last_name: '',
        role: ''
    });

    const [validFields, setValidFields] = useState({
        email: false,
        first_name: false,
        last_name: false,
        role: false
    });

    const validateEmail = (email) => {
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        return emailRegex.test(email);
    };

    const validateField = (field, value) => {
        switch (field) {
            case 'email':
                return value && validateEmail(value);
            case 'first_name':
            case 'last_name':
                return value && value.trim().length > 0;
            case 'role':
                return value && value !== '';
            default:
                return false;
        }
    };

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
        setValidFields(prev => ({
            ...prev,
            [field]: validateField(field, value)
        }));
    };

    const handleSubmit = () => {
        const newErrors = {
            email: !formData.email ? 'Email is required' : 
                   !validateEmail(formData.email) ? 'Please enter a valid email address' : '',
            first_name: !formData.first_name ? 'First name is required' : '',
            last_name: !formData.last_name ? 'Last name is required' : '',
            role: !formData.role ? 'Role is required' : ''
        };

        setErrors(newErrors);

        // Update valid fields
        const newValidFields = {
            email: !newErrors.email,
            first_name: !newErrors.first_name,
            last_name: !newErrors.last_name,
            role: !newErrors.role
        };
        setValidFields(newValidFields);

        // Check if there are any errors
        if (Object.values(newErrors).every(error => !error)) {
            onSubmit();
        }
    };

    return (
    <mui.Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <mui.DialogTitle>
            <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <UserGroupIcon className="h-6 w-6 text-primary" />
                Create New User
            </mui.Box>
        </mui.DialogTitle>
        <mui.DialogContent>
            <mui.Box 
                component="form" 
                sx={{ mt: 2 }}
                autoComplete="off"
                noValidate
                onSubmit={(e) => e.preventDefault()}
            >
                {/* Basic Information Section */}
                <mui.Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <DocumentIcon className="h-5 w-5" />
                    Basic Information
                </mui.Typography>
                <mui.Grid container spacing={3}>
                    <mui.Grid item xs={12}>
                        <mui.TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleFieldChange('email', e.target.value)}
                            autoComplete="new-email"
                            name={`email-${Math.random()}`}
                            InputProps={{
                                startAdornment: (
                                    <mui.InputAdornment position="start">
                                        <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </mui.InputAdornment>
                                ),
                                endAdornment: validFields.email && (
                                    <mui.InputAdornment position="end">
                                        <CheckCircleIcon className="h-5 w-5 text-success" />
                                    </mui.InputAdornment>
                                )
                            }}
                            required
                            error={!!errors.email}
                            helperText={errors.email || "This email will be used as the username for login"}
                        />
                    </mui.Grid>
                </mui.Grid>

                {/* Personal Information Section */}
                <mui.Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 4, mb: 2 }}>
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Personal Information
                </mui.Typography>
                <mui.Grid container spacing={3}>
                    <mui.Grid item xs={12} md={6}>
                        <mui.TextField
                            fullWidth
                            label="First Name"
                            value={formData.first_name}
                            onChange={(e) => handleFieldChange('first_name', e.target.value)}
                            autoComplete="new-first-name"
                            name={`first-name-${Math.random()}`}
                            InputProps={{
                                startAdornment: (
                                    <mui.InputAdornment position="start">
                                        <UserGroupIcon className="h-5 w-5 text-gray-400" />
                                    </mui.InputAdornment>
                                ),
                                endAdornment: validFields.first_name && (
                                    <mui.InputAdornment position="end">
                                        <CheckCircleIcon className="h-5 w-5 text-success" />
                                    </mui.InputAdornment>
                                )
                            }}
                            required
                            error={!!errors.first_name}
                            helperText={errors.first_name}
                        />
                    </mui.Grid>
                    <mui.Grid item xs={12} md={6}>
                        <mui.TextField
                            fullWidth
                            label="Last Name"
                            value={formData.last_name}
                            onChange={(e) => handleFieldChange('last_name', e.target.value)}
                            autoComplete="new-last-name"
                            name={`last-name-${Math.random()}`}
                            InputProps={{
                                startAdornment: (
                                    <mui.InputAdornment position="start">
                                        <UserGroupIcon className="h-5 w-5 text-gray-400" />
                                    </mui.InputAdornment>
                                ),
                                endAdornment: validFields.last_name && (
                                    <mui.InputAdornment position="end">
                                        <CheckCircleIcon className="h-5 w-5 text-success" />
                                    </mui.InputAdornment>
                                )
                            }}
                            required
                            error={!!errors.last_name}
                            helperText={errors.last_name}
                        />
                    </mui.Grid>
                </mui.Grid>

                {/* Access Control Section */}
                <mui.Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 4, mb: 2 }}>
                    <ShieldCheckIcon className="h-5 w-5" />
                    Access Control
                </mui.Typography>
                <mui.Grid container spacing={3}>
                    <mui.Grid item xs={12}>
                        <mui.FormControl fullWidth error={!!errors.role} required>
                            <mui.InputLabel>Role</mui.InputLabel>
                            <mui.Select
                                value={formData.role}
                                onChange={(e) => handleFieldChange('role', e.target.value)}
                                label="Role"
                                autoComplete="new-role"
                                name={`role-${Math.random()}`}
                                endAdornment={
                                    <>
                                        {validFields.role && (
                                            <mui.InputAdornment position="end">
                                                <CheckCircleIcon className="h-5 w-5 text-success" />
                                            </mui.InputAdornment>
                                        )}
                                        <mui.InputAdornment position="end">
                                            <mui.IconButton edge="end">
                                                <mui.Box component="span" sx={{ display: 'flex', mr: 1 }}>
                                                    <ShieldCheckIcon className="h-5 w-5" />
                                                </mui.Box>
                                            </mui.IconButton>
                                        </mui.InputAdornment>
                                    </>
                                }
                            >
                                {roles.map((role) => (
                                    <mui.MenuItem key={role.id} value={role.id}>
                                        {role.role_name}
                                    </mui.MenuItem>
                                ))}
                            </mui.Select>
                            {errors.role && <mui.FormHelperText>{errors.role}</mui.FormHelperText>}
                        </mui.FormControl>
                    </mui.Grid>
                </mui.Grid>
            </mui.Box>
        </mui.DialogContent>
        <mui.DialogActions sx={{ p: 3, backgroundColor: 'background.default' }}>
            <mui.Button 
                onClick={onClose}
                startIcon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>}
            >
                Cancel
            </mui.Button>
            <mui.Button 
                onClick={handleSubmit} 
                variant="contained" 
                color="primary"
                startIcon={<PlusIcon className="h-5 w-5" />}
            >
                Create User
            </mui.Button>
        </mui.DialogActions>
    </mui.Dialog>
    );
});

const EditUserDialog = memo(({ open, onClose, formData, setFormData, roles, onSubmit }) => {
    const [errors, setErrors] = useState({
        role: ''
    });

    const [validFields, setValidFields] = useState({
        role: !!formData.role
    });

    const validateField = (field, value) => {
        switch (field) {
            case 'role':
                return value && value !== '';
            default:
                return false;
        }
    };

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
        setValidFields(prev => ({
            ...prev,
            [field]: validateField(field, value)
        }));
    };

    const handleSubmit = () => {
        const newErrors = {
            role: !formData.role ? 'Role is required' : ''
        };

        setErrors(newErrors);

        // Update valid fields
        const newValidFields = {
            role: !newErrors.role
        };
        setValidFields(newValidFields);

        // Check if there are any errors
        if (Object.values(newErrors).every(error => !error)) {
            onSubmit();
        }
    };

    return (
        <mui.Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <mui.DialogTitle>
                <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <UserGroupIcon className="h-6 w-6 text-primary" />
                    Edit User Access
                </mui.Box>
            </mui.DialogTitle>
            <mui.DialogContent>
                <mui.Box 
                    component="form" 
                    sx={{ mt: 2 }}
                    autoComplete="off"
                    noValidate
                    onSubmit={(e) => e.preventDefault()}
                >
                    {/* Basic Information Section */}
                    <mui.Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <DocumentIcon className="h-5 w-5" />
                        User Information
                    </mui.Typography>
                    <mui.Grid container spacing={3}>
                        <mui.Grid item xs={12}>
                            <mui.TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={formData.email}
                                disabled
                            />
                        </mui.Grid>
                        <mui.Grid item xs={12} md={6}>
                            <mui.TextField
                                fullWidth
                                label="First Name"
                                value={formData.first_name}
                                disabled
                                InputProps={{
                                    startAdornment: (
                                        <mui.InputAdornment position="start">
                                            <UserGroupIcon className="h-5 w-5 text-gray-400" />
                                        </mui.InputAdornment>
                                    )
                                }}
                            />
                        </mui.Grid>
                        <mui.Grid item xs={12} md={6}>
                            <mui.TextField
                                fullWidth
                                label="Last Name"
                                value={formData.last_name}
                                disabled
                                InputProps={{
                                    startAdornment: (
                                        <mui.InputAdornment position="start">
                                            <UserGroupIcon className="h-5 w-5 text-gray-400" />
                                        </mui.InputAdornment>
                                    )
                                }}
                            />
                        </mui.Grid>
                    </mui.Grid>

                    {/* Access Control Section */}
                    <mui.Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 4, mb: 2 }}>
                        <ShieldCheckIcon className="h-5 w-5" />
                        Access Control
                    </mui.Typography>
                    <mui.Grid container spacing={3}>
                        <mui.Grid item xs={12}>
                            <mui.FormControl fullWidth error={!!errors.role} required>
                                <mui.InputLabel>Role</mui.InputLabel>
                                <mui.Select
                                    value={formData.role}
                                    onChange={(e) => handleFieldChange('role', e.target.value)}
                                    label="Role"
                                    autoComplete="new-role"
                                    name={`role-${Math.random()}`}
                                    endAdornment={
                                        <>
                                            {validFields.role && (
                                                <mui.InputAdornment position="end">
                                                    <CheckCircleIcon className="h-5 w-5 text-success" />
                                                </mui.InputAdornment>
                                            )}
                                            <mui.InputAdornment position="end">
                                                <mui.IconButton edge="end">
                                                    <mui.Box component="span" sx={{ display: 'flex', mr: 1 }}>
                                                        <ShieldCheckIcon className="h-5 w-5" />
                                                    </mui.Box>
                                                </mui.IconButton>
                                            </mui.InputAdornment>
                                        </>
                                    }
                                >
                                    {roles.map((role) => (
                                        <mui.MenuItem key={role.id} value={role.id}>
                                            {role.role_name}
                                        </mui.MenuItem>
                                    ))}
                                </mui.Select>
                                {errors.role && <mui.FormHelperText>{errors.role}</mui.FormHelperText>}
                            </mui.FormControl>
                        </mui.Grid>
                    </mui.Grid>
                </mui.Box>
            </mui.DialogContent>
            <mui.DialogActions sx={{ p: 3, backgroundColor: 'background.default' }}>
                <mui.Button 
                    onClick={onClose}
                    startIcon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>}
                >
                    Cancel
                </mui.Button>
                <mui.Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    color="primary"
                    startIcon={<PencilIcon className="h-5 w-5" />}
                >
                    Update Access
                </mui.Button>
            </mui.DialogActions>
        </mui.Dialog>
    );
});

const ManageAccess = () => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [audits, setAudits] = useState([]);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTab, setSelectedTab] = useState(0);
    const { id } = useParams();
    const [selectedProject, setSelectedProject] = useState(null);
    const [clientUsers, setClientUsers] = useState([]);

    // Form states
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        email: '',
        first_name: '',
        last_name: '',
        role: '',
        company: '',
        audit_access: []
    });
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openStatusDialog, setOpenStatusDialog] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [userToToggle, setUserToToggle] = useState(null);

    useEffect(() => {
        fetchInitialData();
        fetchProjectbyID();
        fetchClientUsers();
    }, [id]);

    const fetchProjectbyID = async () => {
        try {
            const projectRecord = await auditService.fetchProjectsById(id)

            if (projectRecord) {
                setSelectedProject(projectRecord);

            } 

        } catch (fetchError) {
            console.error('Error fetching company:', fetchError);
        
        }
    }

    const fetchInitialData = async () => {
        try {
            console.log('Fetching initial data...');
            const [usersData, rolesData] = await Promise.all([
                roleService.fetchUsers(),
                roleService.fetchRoles()
            ]);
            
            console.log('Fetched users data:', usersData);
            console.log('Fetched roles data:', rolesData);
            
            setUsers(usersData);
            setRoles(rolesData);
            
            console.log('State updated with new data');
        } catch (error) {
            console.error('Error fetching data:', error);
            setSnackbar({
                open: true,
                message: 'Error fetching data',
                severity: 'error'
            });
        }
    };

    const fetchClientUsers = async () => {
        try {
            const usersData = await roleService.fetchUsers();
            // Filter users with Client Participant or Client Coordinator roles
            const clientRoleUsers = usersData.filter(user => 
                user.role.role_name === 'Client Participant' || 
                user.role.role_name === 'Client Coordinator'
            ).map(user => ({
                value: `${user.user.first_name} ${user.user.last_name}`,
                label: `${user.user.first_name} ${user.user.last_name} (${user.role.role_name})`
            }));
            setClientUsers(clientRoleUsers);
        } catch (error) {
            console.error('Error fetching client users:', error);
        }
    };

    const handleCreateUser = useCallback(async () => {
        try {
            // Generate a random temporary password
            const tempPassword = Math.random().toString(36).slice(-12) + Math.random().toString(36).slice(-12);
            
            // Clean the project ID if it exists
            const cleanProjectId = id ? id.toString().replace(/['"]/g, '').trim() : null;
            
            // Create user data including audit_access
            const userData = {
                username: formData.email,
                password: tempPassword,
                email: formData.email,
                first_name: formData.first_name,
                last_name: formData.last_name,
                role: formData.role,
                company: formData.company,
                audit_access: cleanProjectId ? [cleanProjectId] : [] // Use cleaned project ID
            };
            
            console.log('Creating user with data:', userData);
            
            // Create the user with audit access
            const response = await roleService.createUser(userData);
            
            // Clear form data
            setFormData({
                email: '',
                first_name: '',
                last_name: '',
                role: '',
                company: ''
            });

            // Refresh the data
            await fetchInitialData();
            
            // Show success message from response or default message
            setSnackbar({
                open: true,
                message: response.message || (response.status === 'success' ? 'Project access granted successfully' : 'User created successfully'),
                severity: 'success'
            });

            // Close the dialog
            setOpenCreateDialog(false);
            
        } catch (error) {
            console.error('Error creating user:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Error creating user',
                severity: 'error'
            });
        }
    }, [formData, id]);

    const handleUpdateUser = async () => {
        try {
            console.log('Updating user with data:', formData);
            
            // Prepare update data
            const updateData = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                role: formData.role,
                company: formData.company,
                is_active: formData.is_active,
                audit_access: formData.audit_access || []
            };
            
            // Update user basic info
            await roleService.updateUser(selectedUser.id, updateData);
            
            fetchInitialData();
            setOpenEditDialog(false);
            setSnackbar({
                open: true,
                message: 'User updated successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error updating user:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Error updating user',
                severity: 'error'
            });
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            const response = await roleService.deleteUser(userToDelete.id, id); // Pass project_id
            
            // Handle the response based on deletion type
            if (response.type === 'full_deletion') {
                setSnackbar({
                    open: true,
                    message: 'User has been completely removed from the system',
                    severity: 'success'
                });
            } else {
                setSnackbar({
                    open: true,
                    message: response.message,
                    severity: 'success'
                });
            }
            
            fetchInitialData();
            setOpenDeleteDialog(false);
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Error removing user access',
                severity: 'error'
            });
        }
    };

    const handleToggleStatusConfirm = async () => {
        try {
            if (userToToggle.is_active) {
                await roleService.deactivateUser(userToToggle.id);
            } else {
                await roleService.activateUser(userToToggle.id);
            }
            fetchInitialData();
            setSnackbar({
                open: true,
                message: `User ${userToToggle.is_active ? 'deactivated' : 'activated'} successfully`,
                severity: 'success'
            });
            setOpenStatusDialog(false);
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Error updating user status',
                severity: 'error'
            });
        }
    };

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
        setSearchTerm(''); // Reset search when changing tabs
    };

    const getFilteredUsersByRole = (roleId) => {
        return users.filter(user => 
            user.role.id === roleId && 
            (user.audit_access || []).includes(id) &&  // Filter by project ID
            (searchTerm === '' || 
                user.user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.user.last_name.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    };

    const UserTable = ({ users }) => (
        <mui.TableContainer 
            sx={{ 
                borderRadius: 2,
                boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                backgroundColor: '#ffffff'
            }}
        >
            <mui.Table>
                <mui.TableHead>
                    <mui.TableRow>
                        <mui.TableCell 
                            sx={{ 
                                py: 2,
                                pl: 3,
                                width: '60%',
                                borderBottom: '1px solid',
                                borderColor: 'rgba(148, 163, 184, 0.1)',
                                backgroundColor: '#f8fafc'
                            }}
                        >
                            <mui.Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                User
                            </mui.Typography>
                        </mui.TableCell>
                        <mui.TableCell 
                            sx={{ 
                                py: 2,
                                width: '20%',
                                borderBottom: '1px solid',
                                borderColor: 'rgba(148, 163, 184, 0.1)',
                                backgroundColor: '#f8fafc'
                            }}
                        >
                            <mui.Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Status
                            </mui.Typography>
                        </mui.TableCell>
                        <mui.TableCell 
                            align="right"
                            sx={{ 
                                py: 2,
                                pr: 3,
                                width: '20%',
                                borderBottom: '1px solid',
                                borderColor: 'rgba(148, 163, 184, 0.1)',
                                backgroundColor: '#f8fafc'
                            }}
                        >
                            <mui.Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Actions
                            </mui.Typography>
                        </mui.TableCell>
                    </mui.TableRow>
                </mui.TableHead>
                <mui.TableBody>
                    {users.length === 0 ? (
                        <mui.TableRow>
                            <mui.TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                                <mui.Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                    No users found in this role.
                                </mui.Typography>
                            </mui.TableCell>
                        </mui.TableRow>
                    ) : (
                        users.map((userData) => (
                            <mui.TableRow 
                                key={userData.id}
                                sx={{ 
                                    '&:hover': { 
                                        backgroundColor: 'rgba(0, 0, 0, 0.01)',
                                        '& .action-buttons': {
                                            opacity: 1
                                        }
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <mui.TableCell 
                                    sx={{ 
                                        pl: 3,
                                        py: 2,
                                        borderBottom: '1px solid',
                                        borderColor: 'rgba(148, 163, 184, 0.1)'
                                    }}
                                >
                                    <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar 
                                            {...stringAvatar(`${userData.user.first_name} ${userData.user.last_name}`)}
                                            sx={{ 
                                                width: 40, 
                                                height: 40,
                                                fontSize: '1rem',
                                                fontWeight: 500
                                            }}
                                        />
                                        <mui.Box sx={{ ml: 2 }}>
                                            <mui.Typography 
                                                variant="subtitle2" 
                                                sx={{ 
                                                    fontWeight: 600,
                                                    color: 'text.primary',
                                                    mb: 0.5
                                                }}
                                            >
                                                {userData.user.first_name} {userData.user.last_name}
                                            </mui.Typography>
                                            <mui.Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: 'text.secondary',
                                                    fontSize: '0.8125rem'
                                                }}
                                            >
                                                {userData.user.email}
                                            </mui.Typography>
                                        </mui.Box>
                                    </mui.Box>
                                </mui.TableCell>
                                <mui.TableCell 
                                    sx={{ 
                                        py: 2,
                                        borderBottom: '1px solid',
                                        borderColor: 'rgba(148, 163, 184, 0.1)'
                                    }}
                                >
                                    <mui.Chip
                                        label={userData.is_active ? 'Active' : 'Inactive'}
                                        color={userData.is_active ? 'success' : 'default'}
                                        size="small"
                                        sx={{ 
                                            minWidth: '80px',
                                            height: '24px',
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            borderRadius: '6px',
                                            backgroundColor: userData.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(148, 163, 184, 0.1)',
                                            color: userData.is_active ? 'rgb(16, 185, 129)' : 'rgb(100, 116, 139)',
                                            '& .MuiChip-label': {
                                                px: 2
                                            }
                                        }}
                                    />
                                </mui.TableCell>
                                <mui.TableCell 
                                    align="right"
                                    sx={{ 
                                        pr: 3,
                                        py: 2,
                                        borderBottom: '1px solid',
                                        borderColor: 'rgba(148, 163, 184, 0.1)'
                                    }}
                                >
                                    <mui.Box 
                                        className="action-buttons"
                                        sx={{ 
                                            display: 'flex', 
                                            gap: 1,
                                            justifyContent: 'flex-end',
                                            opacity: 0.7,
                                            transition: 'opacity 0.2s ease'
                                        }}
                                    >
                                        <mui.Tooltip title="Edit user access" arrow>
                                            <mui.IconButton
                                                onClick={() => {
                                                    setSelectedUser(userData);
                                                    setFormData({
                                                        ...userData.user,
                                                        role: userData.role.id,
                                                        audit_access: userData.audit_access || []
                                                    });
                                                    setOpenEditDialog(true);
                                                }}
                                                size="small"
                                                sx={{ 
                                                    color: 'text.secondary',
                                                    '&:hover': { 
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                        color: 'primary.main'
                                                    }
                                                }}
                                            >
                                                <PencilIcon className="h-4 w-4" />
                                            </mui.IconButton>
                                        </mui.Tooltip>
                                        <mui.Tooltip title={userData.is_active ? "Deactivate user" : "Activate user"} arrow>
                                            <mui.IconButton
                                                onClick={() => {
                                                    setUserToToggle(userData);
                                                    setOpenStatusDialog(true);
                                                }}
                                                size="small"
                                                sx={{ 
                                                    color: userData.is_active ? 'error.main' : 'success.main',
                                                    '&:hover': { 
                                                        backgroundColor: userData.is_active ? 'error.lighter' : 'success.lighter'
                                                    }
                                                }}
                                            >
                                                <KeyIcon className="h-4 w-4" />
                                            </mui.IconButton>
                                        </mui.Tooltip>
                                        <mui.Tooltip title="Delete user" arrow>
                                            <mui.IconButton
                                                onClick={() => {
                                                    setUserToDelete(userData);
                                                    setOpenDeleteDialog(true);
                                                }}
                                                size="small"
                                                sx={{ 
                                                    color: 'error.main',
                                                    '&:hover': { 
                                                        backgroundColor: 'error.lighter'
                                                    }
                                                }}
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </mui.IconButton>
                                        </mui.Tooltip>
                                    </mui.Box>
                                </mui.TableCell>
                            </mui.TableRow>
                        ))
                    )}
                </mui.TableBody>
            </mui.Table>
        </mui.TableContainer>
    );

    const DeleteConfirmationDialog = ({ open, onClose, user }) => (
        <mui.Dialog open={open} onClose={onClose}>
            <mui.DialogTitle>Confirm Access Removal</mui.DialogTitle>
            <mui.DialogContent>
                <mui.Typography variant="body1" gutterBottom>
                    Are you sure you want to remove access for "{user?.user?.first_name} {user?.user?.last_name}" from this project?
                </mui.Typography>
                <mui.Typography variant="body2" color="text.secondary">
                    {user?.audit_access?.length <= 1 
                        ? "This is their only project access. The user account will be completely removed from the system."
                        : "The user will still have access to other projects they are assigned to."}
                </mui.Typography>
            </mui.DialogContent>
            <mui.DialogActions>
                <mui.Button onClick={onClose}>Cancel</mui.Button>
                <mui.Button 
                    onClick={handleDeleteConfirm} 
                    color="error" 
                    variant="contained"
                    startIcon={<TrashIcon className="h-5 w-5" />}
                >
                    Remove Access
                </mui.Button>
            </mui.DialogActions>
        </mui.Dialog>
    );

    const StatusConfirmationDialog = ({ open, onClose, user }) => (
        <mui.Dialog open={open} onClose={onClose}>
            <mui.DialogTitle>Confirm Status Change</mui.DialogTitle>
            <mui.DialogContent>
                <mui.Typography>
                    Are you sure you want to {user?.is_active ? 'deactivate' : 'activate'} the user 
                    "{user?.user?.first_name} {user?.user?.last_name}"?
                </mui.Typography>
            </mui.DialogContent>
            <mui.DialogActions>
                <mui.Button onClick={onClose}>Cancel</mui.Button>
                <mui.Button 
                    onClick={handleToggleStatusConfirm} 
                    color={user?.is_active ? 'error' : 'success'} 
                    variant="contained"
                >
                    {user?.is_active ? 'Deactivate' : 'Activate'}
                </mui.Button>
            </mui.DialogActions>
        </mui.Dialog>
    );

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                    <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href={`/audit/projects/${selectedProject?.id}`}>
                            {selectedProject?.AUDIT_NAME}
                        </mui.Link>
                        <mui.Typography color="text.primary">Manage Access</mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        mb: 4, 
                        mt: 3,
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 2, sm: 0 }
                    }}>
                        <ShieldCheckIcon 
                            className="h-6 w-6 text-primary"
                            style={{ marginRight: '20px', opacity: 0.9 }}
                        />
                        <mui.Box sx={{ flex: 1 }}>
                            <mui.Typography 
                                variant="h4" 
                                component="h1" 
                                sx={{ 
                                    fontWeight: 600, 
                                    color: 'text.primary', 
                                    mb: 1,
                                    fontSize: { xs: '1.5rem', sm: '2rem' }
                                }}
                            >
                                Manage Project Access
                            </mui.Typography>
                            <mui.Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 2,
                                flexWrap: 'wrap'
                            }}>
                                <mui.Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1 
                                }}>
                                  
                                
                                </mui.Box>
                            </mui.Box>
                        </mui.Box>
                    </mui.Box>

                    <mui.Box sx={{ 
                        backgroundColor: '#f8fafc', 
                        borderRadius: 3,
                        minHeight: '80vh',
                        boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)'
                    }}>
                        <mui.Paper 
                            elevation={0}
                            sx={{ 
                                p: 2,
                                height: '100%',
                                backgroundColor: 'transparent'
                            }}
                        >
                            <mui.Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                <mui.Button
                                    variant="contained"
                                    startIcon={<PlusIcon className="h-5 w-5" />}
                                    onClick={() => {
                                        setFormData(prev => ({
                                            ...prev,
                                            role: roles[selectedTab]?.id || ''
                                        }));
                                        setOpenCreateDialog(true);
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        borderRadius: 1,
                                        py: 0.75,
                                        px: 2
                                    }}
                                >
                                    Add User
                                </mui.Button>
                            </mui.Box>

                            <mui.Paper
                                sx={{ 
                                    borderRadius: 2,
                                    backgroundColor: '#ffffff',
                                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                                    overflow: 'hidden'
                                }}
                            >
                                <mui.Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <mui.Tabs 
                                        value={selectedTab} 
                                        onChange={handleTabChange}
                                        sx={{
                                            '& .MuiTab-root': {
                                                textTransform: 'none',
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                minHeight: '48px',
                                                color: 'text.secondary',
                                                '&.Mui-selected': {
                                                    color: 'primary.main'
                                                }
                                            }
                                        }}
                                    >
                                        {roles.map((role, index) => (
                                            <mui.Tooltip 
                                                key={role.id} 
                                                title={role.description || 'No description available'} 
                                                arrow
                                                placement="top"
                                            >
                                                <mui.Tab 
                                                    label={
                                                        <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <ShieldCheckIcon sx={{ fontSize: 20 }} />
                                                            {role.role_name}
                                                        </mui.Box>
                                                    }
                                                />
                                            </mui.Tooltip>
                                        ))}
                                    </mui.Tabs>
                                </mui.Box>

                                <mui.Box sx={{ p: 3 }}>
                                    <mui.TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Search users..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <mui.InputAdornment position="start">
                                                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                                                </mui.InputAdornment>
                                            ),
                                        }}
                                        sx={{ 
                                            mb: 3,
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: 'rgba(148, 163, 184, 0.05)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(148, 163, 184, 0.1)'
                                                }
                                            }
                                        }}
                                    />

                                    {roles.map((role, index) => (
                                        <div key={role.id} style={{ display: selectedTab === index ? 'block' : 'none' }}>
                                            <UserTable users={getFilteredUsersByRole(role.id)} />
                                        </div>
                                    ))}
                                </mui.Box>
                            </mui.Paper>
                        </mui.Paper>
                    </mui.Box>

                    <CreateUserDialog 
                        open={openCreateDialog}
                        onClose={() => setOpenCreateDialog(false)}
                        formData={formData}
                        setFormData={setFormData}
                        roles={roles}
                        onSubmit={handleCreateUser}
                    />
                    <EditUserDialog 
                        open={openEditDialog}
                        onClose={() => setOpenEditDialog(false)}
                        formData={formData}
                        setFormData={setFormData}
                        roles={roles}
                        onSubmit={handleUpdateUser}
                    />
                    <DeleteConfirmationDialog
                        open={openDeleteDialog}
                        onClose={() => setOpenDeleteDialog(false)}
                        user={userToDelete}
                    />
                    <StatusConfirmationDialog
                        open={openStatusDialog}
                        onClose={() => setOpenStatusDialog(false)}
                        user={userToToggle}
                    />
                    <DynamicSnackbar
                        open={snackbar.open}
                        message={snackbar.message}
                        severity={snackbar.severity}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                    />
                </mui.Box>
            </ResponsiveContainer>
        </div>
    );

    return (
        <React.Suspense fallback="Loading...">
            <div>
                <IASideBar mainContent={customMainContent} />
            </div>
        </React.Suspense>
    );
};

export default ManageAccess;